/**
 * `MediaLibraryModel` — see {@link IMediaLibraryModel} for the ownership and
 * scoping notes. Web-first: no Unity counterpart, modelled on
 * `ProjectDocsModel` so the two customer-scoped libraries behave the same.
 *
 * Two things here are deliberate and worth not "simplifying":
 *
 *  - **Uploads get a unique file name** rather than the one the user picked.
 *    The library is a flat, account-wide folder, so two people uploading
 *    `clip.mp4` would otherwise silently overwrite each other's file — and the
 *    loser's workflow would keep playing, showing the wrong video. The readable
 *    stem survives in the name so the url an author pastes still says what it
 *    is.
 *  - **Resolved `blob:` URLs are cached for the tab's life and never revoked
 *    by a consumer.** A step's media can be on screen in the desktop panel and
 *    an XR slate at once, and revisiting a step must not re-download a video.
 *    Same rule `thumbnailCache` and `profilePicCache` already follow.
 */

import { Emitter } from "@core/events/emitter";
import type { IAuthModel } from "@models/auth/IAuthModel";
import { fileExtension } from "@models/data/docsManifestDto";
import { formatDocLastModifiedUtc, parseDocLastModified } from "@models/data/docsManifestDto";
import {
  MEDIA_LIBRARY_EXTENSIONS,
  type MediaLibraryEntryDto,
  type MediaLibraryKind,
  type MediaLibraryManifestDto,
  findMediaEntry,
  mediaContentType,
  mediaKindForFileName,
  mediaLibraryStoragePath,
  mediaLibraryUri,
  parseMediaLibraryManifest,
  parseMediaLibraryUri,
  removeMediaEntry,
  serializeMediaLibraryManifest,
  uniqueMediaFileName,
  upsertMediaEntry,
} from "@models/data/mediaLibraryDto";
import type { IProjectModel } from "@models/project/IProjectModel";
import type { IStorageModel } from "@models/storage/IStorageModel";
import {
  hasCachedObjectBytes,
  isProjectBinaryCacheDisabled,
  readCachedObjectBytes,
  writeCachedObjectBytes,
} from "@models/storage/projectBinaryCache";
import { randomUuid } from "@models/utils/uuid";
import {
  MEDIA_MANIFEST_CACHE_MS,
  type IMediaLibraryModel,
  type MediaLibraryItem,
  type MediaUploadResult,
  type PendingMediaUpload,
} from "./IMediaLibraryModel";

export class MediaLibraryModel implements IMediaLibraryModel {
  readonly onChanged = new Emitter<ReadonlyArray<MediaLibraryItem>>();
  readonly onBusyChanged = new Emitter<boolean>();
  readonly onProgressChanged = new Emitter<string>();
  readonly onPendingChanged = new Emitter<ReadonlyArray<PendingMediaUpload>>();

  private m_items: ReadonlyArray<MediaLibraryItem> = [];
  private m_pending: ReadonlyArray<PendingMediaUpload> = [];
  private m_isBusy = false;
  private m_progress = "";

  private m_manifest: MediaLibraryManifestDto | null = null;
  private m_manifestCustomerId: string | null = null;
  private m_manifestLoadedAtMs = 0;
  private m_manifestLoad: Promise<MediaLibraryManifestDto | null> | null = null;

  /** `{customerId}|{storagePath}` → blob URL (`null` = probed, absent). */
  private readonly m_resolved = new Map<string, string | null>();
  private readonly m_resolveLoads = new Map<string, Promise<string | null>>();

  constructor(
    private readonly m_project: IProjectModel,
    private readonly m_auth: IAuthModel,
    private readonly m_storage: IStorageModel,
  ) {}

  get items(): ReadonlyArray<MediaLibraryItem> {
    return this.m_items;
  }

  get pendingUploads(): ReadonlyArray<PendingMediaUpload> {
    return this.m_pending;
  }

  get isBusy(): boolean {
    return this.m_isBusy;
  }

  get progress(): string {
    return this.m_progress;
  }

  // ---- Load --------------------------------------------------------

  async loadAsync(
    forceReload = false,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<MediaLibraryItem>> {
    if (!this.customerId()) {
      this.setItems([]);
      return this.m_items;
    }

    this.setBusy(true);
    try {
      const manifest = await this.getManifestAsync(forceReload, signal);
      this.setItems(manifest ? buildItems(manifest) : []);
    } catch (err) {
      console.warn("[MediaLibraryModel] loadAsync failed:", err);
      this.setItems([]);
    } finally {
      this.setBusy(false);
    }
    return this.m_items;
  }

  // ---- Upload ------------------------------------------------------

  async uploadAsync(
    files: ReadonlyArray<File>,
    signal?: AbortSignal,
  ): Promise<MediaUploadResult> {
    const customerId = this.customerId();
    if (!customerId) throw new Error("Sign in before uploading media");

    // Split the picked files BEFORE anything async, so the placeholder tiles
    // appear on the same frame as the picker closing. `accept` on an <input> is
    // only a HINT — the user can switch it to "All Files" — so the extension
    // list is re-checked here, and a rejected file never gets a tile: it is not
    // going anywhere, and the toast names it.
    const rejected: string[] = [];
    const queued: Array<{ file: File; kind: MediaLibraryKind; pendingId: string }> = [];
    for (const file of files) {
      const originalName = file.name.trim();
      if (!originalName) continue;
      const kind = mediaKindForFileName(originalName);
      if (kind === null || !MEDIA_LIBRARY_EXTENSIONS.includes(fileExtension(originalName))) {
        rejected.push(originalName);
        continue;
      }
      queued.push({ file, kind, pendingId: randomUuid() });
    }

    if (queued.length === 0) {
      // Nothing to upload — do not raise the busy state over a grid that is not
      // going to change, and do not clear a previous batch's failed tiles.
      return { uploaded: 0, failed: 0, rejected, urls: [] };
    }

    // A new batch replaces the last one's leftovers, failures included: those
    // tiles describe work that is now two picks old.
    this.setPending(
      queued.map(({ file, kind, pendingId }) => ({
        id: pendingId,
        fileName: file.name.trim(),
        kind,
        fileSize: file.size,
        status: "queued" as const,
      })),
    );

    this.setBusy(true);
    try {
      // Force-reload before a read-modify-write: another client (or this one in
      // another tab) may have changed the manifest since the cache.
      const manifest = await this.getManifestAsync(true, signal);
      if (!manifest) throw new Error("Could not read the media library");

      const userTag = this.m_auth.currentUser?.nameTag ?? "";
      const nowUtc = formatDocLastModifiedUtc(new Date());

      let uploaded = 0;
      let failed = 0;
      const urls: string[] = [];

      for (let i = 0; i < queued.length; i++) {
        const { file, kind, pendingId } = queued[i];
        const originalName = file.name.trim();
        const fileName = uniqueMediaFileName(originalName, randomUuid().slice(0, 8));
        const storagePath = mediaLibraryStoragePath(fileName);
        const contentType = mediaContentType(fileName);

        this.setPendingStatus(pendingId, "uploading");
        this.setProgress(`Uploading ${i + 1}/${queued.length} — ${originalName}`);
        try {
          const bytes = new Uint8Array(await file.arrayBuffer());
          await this.m_storage.putMediaLibraryObject(
            customerId,
            storagePath,
            bytes,
            contentType,
            signal,
          );

          const entry: MediaLibraryEntryDto = {
            ...(findMediaEntry(manifest, storagePath) ?? {}),
            FileName: fileName,
            StoragePath: storagePath,
            MediaType: kind,
            ContentType: contentType,
            FileSize: file.size,
            UserTag: userTag,
            LastModifiedUtc: nowUtc,
          };
          upsertMediaEntry(manifest, entry);
          urls.push(mediaLibraryUri(fileName));
          uploaded++;
        } catch (err) {
          console.warn(`[MediaLibraryModel] upload failed for ${originalName}:`, err);
          this.setPendingStatus(pendingId, "failed");
          failed++;
        }
      }

      if (uploaded > 0) {
        this.setProgress("Saving library…");
        await this.saveManifestAsync(manifest, customerId, signal);
        await this.loadAsync(true, signal);
      }
      return { uploaded, failed, rejected, urls };
    } finally {
      this.setProgress("");
      // AFTER the reload above, not before: clearing first would blink the
      // placeholders out and leave the grid without them for the length of the
      // manifest fetch. Failures stay — their tile is the only thing that says
      // which file did not make it.
      this.setPending(this.m_pending.filter((entry) => entry.status === "failed"));
      this.setBusy(false);
    }
  }

  dismissPendingUpload(id: string): void {
    if (!this.m_pending.some((entry) => entry.id === id)) return;
    this.setPending(this.m_pending.filter((entry) => entry.id !== id));
  }

  // ---- Delete ------------------------------------------------------

  async deleteAsync(storagePath: string, signal?: AbortSignal): Promise<void> {
    const normalized = storagePath.trim();
    if (!normalized) return;
    const customerId = this.customerId();
    if (!customerId) throw new Error("Sign in before managing media");

    this.setBusy(true);
    try {
      const manifest = await this.getManifestAsync(true, signal);
      if (!manifest) throw new Error("Could not read the media library");

      // The S3 object goes first: a manifest entry with no bytes behind it is a
      // card that shows nothing, while bytes with no entry are merely an
      // invisible orphan — the same trade every delete path here already makes.
      await this.m_storage.deleteMediaLibraryObject(customerId, normalized, signal);
      removeMediaEntry(manifest, normalized);
      await this.saveManifestAsync(manifest, customerId, signal);
      this.dropResolved(customerId, normalized);
      await this.loadAsync(true, signal);
    } finally {
      this.setBusy(false);
    }
  }

  // ---- Resolve -----------------------------------------------------

  async resolveUrlAsync(url: string, signal?: AbortSignal): Promise<string | null> {
    const trimmed = url.trim();
    if (trimmed.length === 0) return null;

    const storagePath = parseMediaLibraryUri(trimmed);
    // Not a library reference: an external url is what the browser should load.
    if (storagePath === null) return trimmed;

    const customerId = this.customerId();
    if (!customerId) return null;

    const key = `${customerId}|${storagePath.toLowerCase()}`;
    const cached = this.m_resolved.get(key);
    if (cached !== undefined) return cached;
    const inFlight = this.m_resolveLoads.get(key);
    if (inFlight) return inFlight;

    const load = (async () => {
      const blobUrl = await this.readMediaBlobUrlAsync(customerId, storagePath, signal);
      this.m_resolved.set(key, blobUrl);
      return blobUrl;
    })();

    this.m_resolveLoads.set(key, load);
    try {
      return await load;
    } finally {
      this.m_resolveLoads.delete(key);
    }
  }

  async prefetchAsync(
    urls: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void> {
    // The prefetch exists only to FILL the byte cache ahead of playback; with
    // caching disabled (app settings' "Disable caching") every write below
    // no-ops, so running it would download each video in full and discard the
    // bytes — on every project open.
    if (isProjectBinaryCacheDisabled()) return;
    const customerId = this.customerId();
    if (!customerId) return;

    // Load the manifest first so a cached entry is stored under the content
    // type the UPLOAD recorded rather than one guessed from the extension —
    // the type is baked into the cached response and is what a `<video>` is
    // handed on playback. Cheap: cached for `MEDIA_MANIFEST_CACHE_MS`.
    try {
      await this.getManifestAsync(false, signal);
    } catch {
      // Falls back to the extension-derived type, which is right for every
      // format the library accepts.
    }

    const seen = new Set<string>();
    for (const url of urls) {
      if (signal?.aborted) return;
      const storagePath = parseMediaLibraryUri(url.trim());
      // An external url is the browser's to fetch and cache; only library
      // references live in our bucket.
      if (storagePath === null) continue;
      // Keyed RAW, matching the byte cache: S3 is case-sensitive, so
      // `media/Clip.mp4` and `media/clip.mp4` are genuinely different objects
      // and folding them would skip one. The `m_resolved` probe below stays
      // lower-cased because that is that map's own key convention.
      if (seen.has(storagePath)) continue;
      seen.add(storagePath);
      // Already resolved in this tab, so playback will not touch the network.
      if (this.m_resolved.has(`${customerId}|${storagePath.toLowerCase()}`)) continue;

      try {
        if (await hasCachedObjectBytes(customerId, storagePath)) continue;
        const blob = await this.downloadMediaBlobAsync(customerId, storagePath, signal);
        if (!blob) continue;
        // Deliberately NOT put in `m_resolved`: holding a blob URL per video
        // would keep every one of a workflow's videos alive for the tab's
        // life, which is the memory this prefetch exists to avoid spending.
        // Playback reads them back off disk, which is fast.
        writeCachedObjectBytes(customerId, storagePath, blob, blob.type || null);
      } catch (err) {
        if (signal?.aborted) return;
        console.warn(`[MediaLibraryModel] prefetch failed for ${storagePath}:`, err);
      }
    }
  }

  reset(): void {
    for (const url of this.m_resolved.values()) {
      if (url) URL.revokeObjectURL(url);
    }
    this.m_resolved.clear();
    this.m_manifest = null;
    this.m_manifestCustomerId = null;
    this.m_manifestLoadedAtMs = 0;
    this.setItems([]);
    this.setPending([]);
  }

  // ---- Internals ---------------------------------------------------

  private async getManifestAsync(
    forceReload = false,
    signal?: AbortSignal,
  ): Promise<MediaLibraryManifestDto | null> {
    const customerId = this.customerId();
    if (!customerId) return null;

    const fresh =
      this.m_manifest !== null &&
      this.m_manifestCustomerId === customerId &&
      Date.now() - this.m_manifestLoadedAtMs < MEDIA_MANIFEST_CACHE_MS;
    if (fresh && !forceReload) return this.m_manifest;
    if (this.m_manifestLoad && !forceReload) return this.m_manifestLoad;

    this.m_manifestLoad = (async () => {
      try {
        const json = await this.m_storage.getMediaLibraryManifestJson(customerId, signal);
        // `null` here means the object is ABSENT (a fresh account), which is an
        // empty manifest — the storage layer throws on a real failure.
        const manifest = parseMediaLibraryManifest(json);
        this.m_manifest = manifest;
        this.m_manifestCustomerId = customerId;
        this.m_manifestLoadedAtMs = Date.now();
        return manifest;
      } catch (err) {
        // Do NOT cache a failure as "no media": the next upload would write a
        // manifest that drops every existing entry.
        console.warn("[MediaLibraryModel] media manifest read failed:", err);
        this.m_manifest = null;
        this.m_manifestCustomerId = null;
        return null;
      } finally {
        this.m_manifestLoad = null;
      }
    })();
    return this.m_manifestLoad;
  }

  private async saveManifestAsync(
    manifest: MediaLibraryManifestDto,
    customerId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    manifest.Version ??= 1;
    manifest.Media ??= [];
    await this.m_storage.putMediaLibraryManifestJson(
      customerId,
      serializeMediaLibraryManifest(manifest),
      signal,
    );
    this.m_manifest = manifest;
    this.m_manifestCustomerId = customerId;
    this.m_manifestLoadedAtMs = Date.now();
  }

  /**
   * Same precedence as `ProjectDocsModel.cloudContext` and
   * `ProjectModel.getStorageContext`: a cross-account share lives in the
   * OWNING account's partition. Unlike documents this needs no open project —
   * the library is account-wide, and the Media tab is useful before a workflow
   * exists to paste into.
   */
  /**
   * Bytes-first: the persistent cache, then S3 — and a download is kept for
   * the next session, which is what lets a prefetched video play from disk
   * after a reload.
   */
  private async readMediaBlobUrlAsync(
    customerId: string,
    storagePath: string,
    signal?: AbortSignal,
  ): Promise<string | null> {
    const cached = await readCachedObjectBytes(customerId, storagePath, signal);
    if (cached) return URL.createObjectURL(cached.blob);

    const blob = await this.downloadMediaBlobAsync(customerId, storagePath, signal);
    if (!blob) return null;
    writeCachedObjectBytes(customerId, storagePath, blob, blob.type || null);
    return URL.createObjectURL(blob);
  }

  /**
   * `IStorageModel.getMediaLibraryBlobUrl` hands back a blob URL the caller
   * owns; the byte cache needs the Blob itself. Reading it back takes a second
   * reference to the same immutable blob data rather than copying a video.
   *
   * The read-through lives HERE rather than in the storage model because that
   * method delegates to the DOCS reader, and a document is fetched once for a
   * vector-store answer — caching those would be quota spent on nothing. Same
   * rule as `GltfAssetCache`: the consumer that benefits owns the cache.
   */
  private async downloadMediaBlobAsync(
    customerId: string,
    storagePath: string,
    signal?: AbortSignal,
  ): Promise<Blob | null> {
    const contentType =
      findMediaEntry(this.m_manifest, storagePath)?.ContentType ??
      mediaContentType(storagePath.split("/").pop());
    const url = await this.m_storage.getMediaLibraryBlobUrl(
      customerId,
      storagePath,
      contentType,
      signal,
    );
    if (!url) return null;
    try {
      return await (await fetch(url, { signal })).blob();
    } catch (err) {
      if (!signal?.aborted) {
        console.warn(`[MediaLibraryModel] media read failed for ${storagePath}:`, err);
      }
      return null;
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  private customerId(): string | null {
    if (!this.m_storage.isOnline) return null;
    return this.m_project.activeProject?.customerId ?? this.m_auth.currentUser?.customerId ?? null;
  }

  private dropResolved(customerId: string, storagePath: string): void {
    const key = `${customerId}|${storagePath.toLowerCase()}`;
    const url = this.m_resolved.get(key);
    if (url) URL.revokeObjectURL(url);
    this.m_resolved.delete(key);
  }

  private setItems(items: ReadonlyArray<MediaLibraryItem>): void {
    this.m_items = items;
    this.onChanged.emit(items);
  }

  private setPending(pending: ReadonlyArray<PendingMediaUpload>): void {
    this.m_pending = pending;
    this.onPendingChanged.emit(pending);
  }

  private setPendingStatus(id: string, status: PendingMediaUpload["status"]): void {
    this.setPending(
      this.m_pending.map((entry) => (entry.id === id ? { ...entry, status } : entry)),
    );
  }

  private setBusy(busy: boolean): void {
    if (this.m_isBusy === busy) return;
    this.m_isBusy = busy;
    this.onBusyChanged.emit(busy);
  }

  private setProgress(progress: string): void {
    if (this.m_progress === progress) return;
    this.m_progress = progress;
    this.onProgressChanged.emit(progress);
  }
}

function buildItems(manifest: MediaLibraryManifestDto): MediaLibraryItem[] {
  const items: MediaLibraryItem[] = [];
  for (const entry of manifest.Media ?? []) {
    const storagePath = entry.StoragePath?.trim();
    const fileName = entry.FileName?.trim();
    if (!storagePath || !fileName) continue;

    // The stored kind wins, with the extension as the fallback — a manifest
    // written by an older build may not carry one.
    const kind =
      entry.MediaType === "image" || entry.MediaType === "video"
        ? entry.MediaType
        : (mediaKindForFileName(fileName) ?? "image");

    items.push({
      storagePath,
      fileName,
      kind,
      format: (fileExtension(fileName) || "media").toUpperCase(),
      fileSize: typeof entry.FileSize === "number" ? entry.FileSize : 0,
      userTag: entry.UserTag?.trim() ?? "",
      lastModifiedMs: parseDocLastModified(entry.LastModifiedUtc),
      url: mediaLibraryUri(fileName),
      contentType: entry.ContentType?.trim() || mediaContentType(fileName),
    });
  }

  items.sort((a, b) => b.lastModifiedMs - a.lastModifiedMs);
  return items;
}
